import React from 'react'
import styled from 'styled-components'
import { Card, List, Tag, Empty, Tooltip, Badge } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'

const PanelContainer = styled.div`
  margin-bottom: 24px;
`

const SectionTitle = styled.h3`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const MenuCard = styled(Card)`
  margin-bottom: 12px;

  .menu-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .menu-name {
    font-weight: 500;
  }

  .section-list {
    max-height: 200px;
    overflow-y: auto;
  }

  .section-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }
  }

  .section-name {
    flex: 1;
    margin-left: 16px;
  }

  .section-actions {
    display: flex;
    gap: 8px;
  }

  &.already-assigned {
    border-left: 4px solid #52c41a;
    background-color: #f6ffed;
  }
`

const MenusAndSectionsPanel = ({ recipe }) => {
  const { menus } = useSelector(state => state.menus)
  const { sections } = useSelector(state => state.sections || { sections: [] })

  console.log('MenusAndSectionsPanel render', { recipe, menus, sections })

  // Get menu IDs where recipe is already assigned
  const getAssignedMenuIds = () => {
    if (!recipe?.menuInfo) return []
    return recipe.menuInfo.filter(
      (value, index, self) => self.indexOf(value) === index,
    )
  }

  const assignedMenuIds = getAssignedMenuIds()

  // Get section IDs where recipe is assigned
  const getAssignedSectionIds = () => {
    if (!recipe?.sectionIds) return []
    return recipe.sectionIds.filter(
      (value, index, self) => self.indexOf(value) === index,
    )
  }

  const assignedSectionIds = getAssignedSectionIds()

  // Get sections for each menu
  const getSectionsForMenu = menuId => {
    return sections.filter(
      section =>
        section.menuId === menuId && assignedSectionIds.includes(section._id),
    )
  }

  // Get menu info with sections
  const getMenuWithSections = () => {
    return assignedMenuIds
      .map(menuId => {
        const menu = menus.find(m => m._id === menuId)
        if (!menu) return null

        const menuSections = getSectionsForMenu(menuId)
        return {
          ...menu,
          sections: menuSections,
        }
      })
      .filter(Boolean)
  }

  const menusWithSections = getMenuWithSections()

  return (
    <PanelContainer>
      <SectionTitle>
        Menus & Sections
        <Tooltip title='Shows which menus and sections this recipe is currently assigned to.'>
          <InfoCircleOutlined style={{ color: '#1890ff' }} />
        </Tooltip>
      </SectionTitle>

      {menusWithSections.length > 0 ? (
        <List
          dataSource={menusWithSections}
          renderItem={menu => (
            <MenuCard key={menu._id} size='small' className='already-assigned'>
              <div className='menu-header'>
                <div className='menu-name'>
                  {menu.name}
                  <Badge
                    count={menu.sections.length}
                    style={{ backgroundColor: '#52c41a', marginLeft: '8px' }}
                  />
                </div>
                <Tag color='green'>Currently in menu</Tag>
              </div>

              {menu.sections.length > 0 && (
                <div className='section-list'>
                  <List
                    size='small'
                    dataSource={menu.sections}
                    renderItem={section => (
                      <div key={section._id} className='section-item'>
                        <div className='section-name'>
                          <Tag color='blue' size='small'>
                            {section.name}
                          </Tag>
                          {section.description && (
                            <span
                              style={{
                                fontSize: '12px',
                                color: '#666',
                                marginLeft: '8px',
                              }}
                            >
                              {section.description}
                            </span>
                          )}
                        </div>
                        <Tag color='cyan' size='small'>
                          In Section
                        </Tag>
                      </div>
                    )}
                  />
                </div>
              )}
            </MenuCard>
          )}
        />
      ) : (
        <Card size='small'>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div
              style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}
            >
              Currently In No Active Menus
            </div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              This recipe is not assigned to any menus or sections yet.
            </div>
          </div>
        </Card>
      )}

      {menus?.length === 0 && (
        <Empty
          description='No menus available'
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </PanelContainer>
  )
}

export default MenusAndSectionsPanel
